import { useState, lazy, Suspense } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useOrgParameter, useArchiveParameter } from "@/hooks/use-parameters";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { ParameterSourceBadge } from "@/components/parameters/parameter-source-badge";
import { DefaultLimitsSection } from "@/components/parameters/default-limits-section";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Badge,
  PageSkeleton,
  Skeleton,
} from "@/components/ui";

const LazyParameterResultsTab = lazy(() =>
  import("@/components/parameters/parameter-results-tab").then((mod) => ({
    default: mod.ParameterResultsTab,
  })),
);

type ParameterDetailTab = "details" | "results";

export const Route = createFileRoute("/_authenticated/parameters/$parameterId/")({
  component: ParameterDetailPage,
});

function ParameterDetailPage() {
  const { t } = useTranslation("parameters");
  const navigate = useNavigate();
  const toasts = useToast();
  const { parameterId } = Route.useParams();

  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: parameter, isLoading, isError } = useOrgParameter(orgId, parameterId);
  useRecentPageTitle(parameter?.name ?? undefined);
  const archiveMutation = useArchiveParameter(orgId, parameterId);

  const isCustom = parameter?.source === "custom";

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ParameterDetailTab>("details");

  const handleArchiveToggle = () => {
    setArchiveError(null);
    setShowArchiveConfirm(true);
  };

  const handleConfirmArchive = () => {
    if (!parameter) return;
    const newStatus = parameter.is_active;
    archiveMutation.mutate(
      { is_active: !newStatus },
      {
        onSuccess: () => {
          setShowArchiveConfirm(false);
          toasts.success(
            newStatus ? "parameters:messages.archiveSuccess" : "parameters:messages.restoreSuccess",
          );
        },
        onError: (err: unknown) => {
          if (err && typeof err === "object" && "status" in err && err.status === 409) {
            setArchiveError(t("messages.archiveBlocked"));
          } else {
            toasts.error(
              newStatus ? "parameters:messages.archiveFailed" : "parameters:messages.restoreFailed",
            );
          }
          setShowArchiveConfirm(false);
        },
      },
    );
  };

  const breadcrumbs = [
    { label: t("title"), to: "/parameters" },
    { label: parameter?.name ?? parameterId },
  ];

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !parameter) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader title={t("messages.notFound")} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        title={parameter.name}
        actions={
          isCustom ? (
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  void navigate({
                    to: "/parameters/$parameterId/edit",
                    params: { parameterId },
                  })
                }
              >
                {t("actions.edit")}
              </Button>
              <Button
                variant="secondary"
                onClick={handleArchiveToggle}
                disabled={archiveMutation.isPending}
              >
                {parameter.is_active ? t("actions.archive") : t("actions.restore")}
              </Button>
            </div>
          ) : undefined
        }
      />

      {archiveError && (
        <div className="mt-4 rounded-md bg-danger/10 p-3 text-sm text-danger">{archiveError}</div>
      )}

      {/* Tabs */}
      <div className="mt-6 flex border-b border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setActiveTab("details")}
          className={`px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === "details"
              ? "border-b-2 border-primary text-primary"
              : "text-muted hover:text-on-surface"
          }`}
        >
          {t("details", { ns: "common", defaultValue: "Details" })}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("results")}
          className={`px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === "results"
              ? "border-b-2 border-primary text-primary"
              : "text-muted hover:text-on-surface"
          }`}
        >
          {t("results.title", { ns: "sampling" })}
        </button>
      </div>

      {activeTab === "results" && (
        <div className="mt-6">
          <Suspense fallback={<Skeleton className="h-48 w-full" />}>
            <LazyParameterResultsTab parameterId={parameterId} />
          </Suspense>
        </div>
      )}

      {activeTab === "details" && (
        <Card className="mt-6">
          <CardContent className="space-y-4 p-6">
            <DetailRow
              label={t("columns.code")}
              value={<span className="font-mono">{parameter.code}</span>}
            />
            <DetailRow label={t("columns.name")} value={parameter.name} />
            <DetailRow label={t("columns.unit")} value={parameter.unit} />
            <DetailRow
              label={t("columns.category")}
              value={
                parameter.category ? t(`categories.${categoryI18nKey(parameter.category)}`) : "-"
              }
            />
            <DetailRow
              label={t("columns.source")}
              value={<ParameterSourceBadge source={parameter.source} />}
            />
            <DetailRow
              label={t("columns.active")}
              value={
                <Badge variant={parameter.is_active ? "success" : "default"}>
                  {parameter.is_active ? t("status.activeStatus") : t("status.archivedStatus")}
                </Badge>
              }
            />
            {parameter.epa_storet_code && (
              <DetailRow
                label={t("columns.epaStoretCode")}
                value={<span className="font-mono text-xs">{parameter.epa_storet_code}</span>}
              />
            )}
            {parameter.cas_number && (
              <DetailRow label={t("columns.casNumber")} value={parameter.cas_number} />
            )}
            {parameter.description && (
              <DetailRow label={t("columns.description")} value={parameter.description} />
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "details" && (
        <DefaultLimitsSection parameterId={parameterId} isCustom={isCustom} />
      )}

      <ConfirmDialog
        open={showArchiveConfirm}
        title={parameter.is_active ? t("actions.archive") : t("actions.restore")}
        message={parameter.is_active ? t("messages.archiveConfirm") : t("messages.restoreConfirm")}
        confirmLabel={parameter.is_active ? t("actions.archive") : t("actions.restore")}
        cancelLabel={t("actions.cancel")}
        onConfirm={handleConfirmArchive}
        onCancel={() => setShowArchiveConfirm(false)}
        variant={parameter.is_active ? "danger" : "primary"}
      />
    </div>
  );
}

function categoryI18nKey(category: string): string {
  const map: Record<string, string> = {
    process_control: "processControl",
  };
  return map[category] ?? category;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-4">
      <dt className="w-40 shrink-0 text-sm font-medium text-muted">{label}</dt>
      <dd className="text-sm text-on-surface">{value}</dd>
    </div>
  );
}
